import { Alert, Box, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../utils/hooks';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { EloSecondaryButton } from '../../components/BergButtons';
import { getEmbeddedQrScanner } from '../../modules/layout/selectors';
import axiosInstance from '../../utils/axios';
import { format } from 'date-fns';

interface AttractionItem {
  productId: string;
  productName: string;
  consumerCategoryId: string;
  consumerCategoryName: string;
  quantity: number;
  orderItemPrice: {
    grossAmount: string;
    currency: string;
  };
}

interface ContactData {
  name: string | null;
  lastName: string | null;
  phone: string | null;
  mail: string | null;
}

interface OnlineOrder {
  id: string;
  status: string;
  attractionsOrder: { items: AttractionItem[] } | null;
  contactData: ContactData | null;
  creationDate: string;
}

interface GroupedOrderItem {
  key: string;
  productName: string;
  consumerCategoryName: string;
  quantity: number;
}

const OnlineOrderScanner = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const embeddedQrScanner = useAppSelector((x) => getEmbeddedQrScanner(x.layout));

  const [order, setOrder] = useState<OnlineOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [openError, setOpenError] = useState<boolean>(false);

  // Group items by productName and consumerCategoryName
  const groupedOrderItems = useMemo((): GroupedOrderItem[] => {
    if (!order?.attractionsOrder?.items) return [];

    const groups = new Map<string, GroupedOrderItem>();

    order.attractionsOrder.items.forEach((item) => {
      const key = `${item.productName}-${item.consumerCategoryName}`;
      const existing = groups.get(key);

      if (existing) {
        existing.quantity += item.quantity;
      } else {
        groups.set(key, {
          key,
          productName: item.productName,
          consumerCategoryName: item.consumerCategoryName,
          quantity: item.quantity,
        });
      }
    });

    return Array.from(groups.values());
  }, [order]);

  const fetchOrder = async (orderId: string) => {
    setLoading(true);
    setOrder(null);
    try {
      const response = await axiosInstance.get<OnlineOrder>(
        `https://api.dev.ecommerce.bergregions.pl/api/v1/order/${orderId}`
      );
      console.log('Order fetched from dev:', response.data);
      setOrder(response.data);
    } catch (devError: any) {
      console.log('Order not found in dev, trying production...');
      try {
        const response = await axiosInstance.get<OnlineOrder>(
          `https://api.ecommerce.bergregions.pl/api/v1/order/${orderId}`
        );
        console.log('Order fetched from production:', response.data);
        setOrder(response.data);
      } catch (prodError: any) {
        console.error('Error fetching order from both APIs:', prodError);
        const message = prodError?.response?.data?.message || t('common:onlineOrderScanner.fetchError');
        setErrorMessage(message);
        setOpenError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQrScan = useCallback(
    (data: string) => {
      if (!data) {
        console.log('Empty QR data received');
        return;
      }

      console.log('QR scan received:', data);
      let orderId = data.trim();

      if (orderId.includes('/')) {
        const parts = orderId.split('/');
        orderId = parts[parts.length - 1];
      }

      if (orderId) {
        fetchOrder(orderId);
      }
    },
    [t]
  );

  useEffect(() => {
    if (window.BtApp) {
      console.log('Assign Online Order handleQrReader');
      window.BtApp.handleQrReader = handleQrScan;
    }
    return () => {
      if (window.BtApp && window.BtApp.handleQrReader) {
        console.log('Unregister Online Order handleQrReader func');
        window.BtApp.handleQrReader = undefined;
      }
    };
  }, [embeddedQrScanner, handleQrScan]);

  const handleCloseError = () => {
    setOpenError(false);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const hasContactData = (contact: ContactData | null): boolean => {
    if (!contact) return false;
    return !!(contact.name || contact.lastName || contact.phone || contact.mail);
  };

  return (
    <Stack direction="column" sx={{ height: '100%', py: 1 }} spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <EloSecondaryButton onClick={() => navigate('/')}>
          {t('common:buttons.back')}
        </EloSecondaryButton>
      </Stack>

      <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, p: 1.5, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: '#27AE60', fontWeight: 'bold' }}>
          {t('common:onlineOrderScanner.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('common:onlineOrderScanner.useQrScanner')}
        </Typography>
      </Box>

      {loading && (
        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 1 }}>{t('common:onlineOrderScanner.loading')}</Typography>
        </Stack>
      )}

      {!loading && !order && (
        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, backgroundColor: '#fafafa', borderRadius: 1, p: 2 }}>
          <Typography variant="body1" color="text.secondary">
            {t('common:onlineOrderScanner.waitingForScan')}
          </Typography>
        </Stack>
      )}

      {!loading && order && (
        <Stack direction="column" spacing={1} sx={{ flex: 1, overflow: 'auto', backgroundColor: '#fafafa', borderRadius: 1, p: 1 }}>
          {/* Order Status Banner */}
          <Box sx={{ backgroundColor: '#27AE60', borderRadius: 1, p: 1.5, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('common:onlineOrderScanner.onlineOrder')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'white' }}>
              {t('common:onlineOrderScanner.paidAndReady')}
            </Typography>
          </Box>

          {/* Order Info */}
          <Box sx={{ backgroundColor: 'white', borderRadius: 1, px: 1.5, py: 1, boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="body2" color="text.secondary">
              {t('common:onlineOrderScanner.orderId')}: {order.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('common:onlineOrderScanner.orderDate')}: {formatDate(order.creationDate)}
            </Typography>
          </Box>

          {/* Contact Data */}
          {hasContactData(order.contactData) && (
            <Box sx={{ backgroundColor: 'white', borderRadius: 1, px: 1.5, py: 1, boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)' }}>
              <Typography variant="body2" fontWeight="bold">
                {t('common:onlineOrderScanner.contactData')}
              </Typography>
              {(order.contactData?.name || order.contactData?.lastName) && (
                <Typography variant="body2">
                  {[order.contactData?.name, order.contactData?.lastName].filter(Boolean).join(' ')}
                </Typography>
              )}
              {order.contactData?.phone && (
                <Typography variant="body2" color="text.secondary">
                  {t('common:onlineOrderScanner.phone')}: {order.contactData.phone}
                </Typography>
              )}
              {order.contactData?.mail && (
                <Typography variant="body2" color="text.secondary">
                  {t('common:onlineOrderScanner.email')}: {order.contactData.mail}
                </Typography>
              )}
            </Box>
          )}

          {/* Grouped Order Items */}
          {groupedOrderItems.length > 0 && (
            <>
              <Typography variant="body2" fontWeight="bold" sx={{ px: 0.5 }}>
                {t('common:onlineOrderScanner.orderedItems')}
              </Typography>
              {groupedOrderItems.map((item) => (
                <Box
                  key={item.key}
                  sx={{ backgroundColor: 'white', borderRadius: 1, px: 1.5, py: 1, boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)' }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" fontWeight="bold" noWrap>
                        {item.productName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {item.consumerCategoryName}
                      </Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight="bold" sx={{ ml: 1 }}>
                      x{item.quantity}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </>
          )}
        </Stack>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openError}
        onClose={handleCloseError}
        autoHideDuration={5000}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default OnlineOrderScanner;
